import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, Phone, Globe, MapPin, HelpCircle, DollarSign, Car, CreditCard, Users, Shield, Wrench, Clock, Home, TrendingUp, ArrowRight } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const DiscoveryFAQsPage = () => {
  useSEO('discovery-faqs', 'Car Buying in Austin, Texas - Discovery FAQs | We Have Your Car');
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const categories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'credit', name: 'Credit Score & Financing', icon: CreditCard },
    { id: 'downpayment', name: 'Down Payments', icon: DollarSign },
    { id: 'pricing', name: 'Vehicle Pricing & Value', icon: TrendingUp },
    { id: 'reliability', name: 'Vehicle Reliability', icon: Shield },
    { id: 'buying', name: 'Buying Process', icon: Car },
    { id: 'tradein', name: 'Trade-Ins', icon: ArrowRight },
    { id: 'firsttime', name: 'First-Time Buyers', icon: Users },
    { id: 'payments', name: 'Payment & Affordability', icon: DollarSign },
    { id: 'austin', name: 'Austin-Specific', icon: MapPin },
    { id: 'inspection', name: 'Inspection & Mechanical', icon: Wrench },
    { id: 'warranty', name: 'Warranty & Protection', icon: Shield },
    { id: 'timing', name: 'Timing & Market', icon: Clock },
  ];

  const faqs = [
    // CREDIT SCORE & FINANCING QUESTIONS
    {
      id: 'credit-1',
      category: 'credit',
      question: 'What credit score do I need to buy a car in Austin, Texas?',
      answer: 'While many dealerships require credit scores of 650 or higher for favorable financing, you can buy a car in Austin with a much lower credit score. Some dealerships like We Have Your Car in Austin work with credit scores as low as 500 or even below, offering specialized financing for customers with bad credit, no credit, or past credit challenges. The minimum credit score varies by lender and dealership - call around to find dealerships that specialize in all credit situations.'
    },
    {
      id: 'credit-2',
      category: 'credit',
      question: 'Can I buy a car with a 500 credit score in Austin?',
      answer: 'Yes, you can buy a car with a 500 credit score in Austin. While traditional lenders may decline you, dealerships like We Have Your Car specialize in bad credit financing and work with lenders who approve customers with scores of 500 or below. You may face higher interest rates or need a larger down payment, but financing is available. Call (512) 666-0016 or visit dealerships that advertise "all credit welcome" or "bad credit financing."'
    },
    {
      id: 'credit-3',
      category: 'credit',
      question: 'Can I buy a car with a 550 credit score in Austin?',
      answer: 'Yes, a 550 credit score is sufficient to buy a car in Austin if you work with the right dealership. We Have Your Car and other bad credit specialists regularly approve customers with 550 credit scores. To improve your approval odds: have proof of steady income, save for a down payment (even $500-$1,000 helps), have references ready, and apply to dealerships that specialize in bad credit rather than traditional new car dealerships.'
    },
    {
      id: 'credit-4',
      category: 'credit',
      question: 'Can I buy a car with no credit history in Austin?',
      answer: 'Yes, you can buy a car in Austin with no credit history. First-time buyers and people who haven\'t used credit before can still get approved. Dealerships like We Have Your Car work with lenders who specialize in no-credit and first-time buyer situations. You\'ll need proof of income, proof of residence, a valid driver\'s license, and possibly references. Having a down payment helps but isn\'t always required.'
    },
    {
      id: 'credit-5',
      category: 'credit',
      question: 'How can I get approved for a car loan with bad credit in Austin?',
      answer: 'To get approved for a car loan with bad credit in Austin: (1) Work with dealerships that specialize in bad credit financing like We Have Your Car at (512) 666-0016, (2) Have proof of steady income (pay stubs, bank statements), (3) Save for a down payment if possible, (4) Have your driver\'s license, proof of residence, and references ready, (5) Be realistic about your budget - approval is easier on affordable vehicles, (6) Apply online or in person - many bad credit specialists offer approval in minutes.'
    },
    {
      id: 'credit-6',
      category: 'credit',
      question: "What's better for bad credit: buy here pay here or dealership financing in Austin?",
      answer: 'Both options work for bad credit in Austin, but have different pros and cons. Buy here pay here means the dealership is the lender - simpler approval but often higher interest rates and limited vehicle selection. Dealership financing (where the dealer works with multiple lenders) like We Have Your Car offers typically means more competitive rates, more vehicle choices, and potentially better terms. We Have Your Car works with multiple lenders to find the best option for your situation. Call (512) 666-0016 to compare options.'
    },
    {
      id: 'credit-7',
      category: 'credit',
      question: 'Can I get a car loan in Austin if I filed bankruptcy?',
      answer: 'Yes, you can get a car loan in Austin after bankruptcy. Many Austin dealerships, including We Have Your Car, work with customers who have Chapter 7 or Chapter 13 bankruptcy on their credit report. Timing matters - the further past your discharge date, the better your chances. Having steady income and a down payment helps. Bankruptcy doesn\'t automatically disqualify you from auto financing.'
    },
    {
      id: 'credit-8',
      category: 'credit',
      question: 'Can I get a car loan in Austin if I had a repossession?',
      answer: 'Yes, you can get a car loan in Austin even with a repossession on your credit. We Have Your Car and other bad credit specialists work with customers who\'ve had repossessions. You may need a larger down payment and will likely face higher interest rates, but financing is available. Being upfront about your situation and working with specialized lenders improves your chances.'
    },
    {
      id: 'credit-9',
      category: 'credit',
      question: 'Does self-employment affect car loan approval in Austin?',
      answer: 'Self-employment doesn\'t prevent car loan approval in Austin, but you\'ll need stronger documentation. We Have Your Car and other dealerships work with self-employed buyers. Bring: recent bank statements (2-3 months), tax returns (1-2 years), profit/loss statements, business license or documentation, and proof of consistent deposits. Self-employed applicants may need larger down payments but can absolutely get approved.'
    },
    {
      id: 'credit-10',
      category: 'credit',
      question: 'How much income do I need to buy a car in Austin?',
      answer: 'Most lenders in Austin look for monthly income of at least $1,500-$2,000, but requirements vary by dealership and lender. Dealerships like We Have Your Car that specialize in flexible financing may work with lower incomes if you have a cosigner or larger down payment. What matters most is steady, provable income and a debt-to-income ratio that allows for car payments.'
    },

    // DOWN PAYMENT QUESTIONS
    {
      id: 'down-1',
      category: 'downpayment',
      question: 'How much down payment do I need to buy a car in Austin?',
      answer: 'Down payment requirements in Austin vary widely. Traditional dealerships may require 10-20% down. Bad credit specialists like We Have Your Car offer more flexible down payments - sometimes as low as $500-$1,000 depending on your credit and the vehicle. Better credit typically means lower down payment requirements. Call specific dealerships like We Have Your Car at (512) 666-0016 to ask about minimum down payments for your situation.'
    },
    {
      id: 'down-2',
      category: 'downpayment',
      question: 'Can I buy a car with $500 down in Austin?',
      answer: 'Yes, some Austin dealerships accept $500 down payments, especially for customers with bad or no credit. We Have Your Car offers flexible down payment options based on your credit situation and the vehicle you choose. The $500 down payment is more common on lower-priced vehicles (under $5,000-$7,000) and for customers working with bad credit financing. Call (512) 666-0016 to ask about current $500 down options.'
    },
    {
      id: 'down-3',
      category: 'downpayment',
      question: 'Can I buy a car with $1,000 down in Austin?',
      answer: 'Yes, $1,000 down is a common down payment amount in Austin for affordable used vehicles. We Have Your Car and similar dealerships regularly work with $1,000 down payments, especially on vehicles priced under $8,000. Your credit situation affects what vehicle you can buy with $1,000 down - better credit may qualify for more expensive vehicles, while bad credit may limit selection to more affordable options.'
    },
    {
      id: 'down-4',
      category: 'downpayment',
      question: 'Can I buy a car with no money down in Austin?',
      answer: 'No money down car purchases are rare in Austin and typically require excellent credit (700+) and strong income. Most Austin dealerships, including bad credit specialists like We Have Your Car, require at least some down payment - often $500-$1,000 minimum. If you have absolutely no money for a down payment, consider: waiting to save at least $500, finding a cosigner with good credit, trading in a vehicle, or working with community programs that assist with transportation.'
    },
    {
      id: 'down-5',
      category: 'downpayment',
      question: 'How much should I put down on a $5,000 car?',
      answer: 'On a $5,000 car in Austin, typical down payments range from $500 to $1,500 (10-30%). The exact amount depends on your credit - bad credit usually requires higher down payments (20-30% or $1,000-$1,500), while better credit may allow 10-15% down ($500-$750). We Have Your Car works with flexible down payments based on your situation. A larger down payment means lower monthly payments and better approval odds.'
    },
    {
      id: 'down-6',
      category: 'downpayment',
      question: 'Does a larger down payment help approval odds in Austin?',
      answer: 'Yes, a larger down payment significantly improves approval chances in Austin, especially with bad credit. A bigger down payment shows commitment, reduces the lender\'s risk, lowers your monthly payments (making approval easier), may qualify you for better interest rates, and gives you equity immediately. If you\'re borderline for approval at dealerships like We Have Your Car, increasing your down payment by even $200-$500 can make the difference.'
    },

    // VEHICLE PRICING & VALUE QUESTIONS
    {
      id: 'pricing-1',
      category: 'pricing',
      question: "What's a good price for a used car in Austin, Texas?",
      answer: 'Good used car prices in Austin vary by year, make, model, and condition. Generally: under $5,000 gets you basic, older transportation (2005-2010 models with higher mileage), $5,000-$8,000 gets you more reliable options with better features (like at We Have Your Car, pricing $4,250-$7,490), $8,000-$12,000 gets you newer vehicles with lower mileage. Austin prices run slightly higher than national averages due to demand. Compare prices across multiple dealerships and check online listings.'
    },
    {
      id: 'pricing-2',
      category: 'pricing',
      question: 'Where can I find the cheapest used cars in Austin?',
      answer: 'The cheapest used cars in Austin are typically found at: independent used car dealerships like We Have Your Car (starting around $4,250), private party sales (Craigslist, Facebook Marketplace), dealerships specializing in high-mileage or older vehicles, buy here pay here lots, and auction purchases. "Cheapest" doesn\'t always mean best value - make sure any cheap car is inspected and reliable. We Have Your Car offers affordable vehicles ($4,250-$7,490) that are inspected and ready to drive.'
    },
    {
      id: 'pricing-3',
      category: 'pricing',
      question: 'Are cars cheaper in Austin or other Texas cities?',
      answer: 'Cars in Austin are typically 5-15% more expensive than smaller Texas cities due to higher demand and wealthier market. Compared to Houston, Dallas, or San Antonio, Austin prices run slightly higher on average. However, selection is better in Austin, and dealerships like We Have Your Car offer competitive pricing. If buying from another city, factor in travel costs, potential issues with out-of-area purchases, and convenience of local service.'
    },
    {
      id: 'pricing-4',
      category: 'pricing',
      question: "What's the best value used car to buy in Austin?",
      answer: 'Best value used cars in Austin are typically: Toyota Camry/Corolla (reliable, cheap to maintain, good resale value), Honda Civic/Accord (similar to Toyota - proven reliability), Ford Focus/Fusion (affordable, parts readily available), Mazda3 (fun to drive, reliable), and Hyundai Elantra/Sonata (good warranty, cheap). We Have Your Car typically stocks reliable brands like Toyota, Honda, and Ford in the $4,250-$7,490 range. Focus on reliability over features for best value.'
    },
    {
      id: 'pricing-5',
      category: 'pricing',
      question: 'How much should I pay for a 2008-2010 Honda Civic in Austin?',
      answer: 'A 2008-2010 Honda Civic in Austin typically costs $5,000-$8,000 depending on mileage, condition, and trim level. Lower mileage (under 100k): $7,000-$8,000, medium mileage (100k-150k): $5,000-$7,000, higher mileage (150k+): $4,000-$5,000. Prices vary by features (EX vs LX trim) and condition. Check dealerships like We Have Your Car for current Honda inventory and pricing. Always get a pre-purchase inspection.'
    },
    {
      id: 'pricing-6',
      category: 'pricing',
      question: 'How much should I pay for a 2005-2007 Toyota Camry in Austin?',
      answer: "A 2005-2007 Toyota Camry in Austin typically costs $4,500-$7,000. Factors affecting price: mileage (under 150k miles costs more), condition (clean title, no accidents), trim level (LE, SE, XLE), and features. We Have Your Car has had similar Toyotas in this price range. Camrys are reliable and hold value well, making them good choices even with higher mileage if well-maintained."
    },

    // VEHICLE RELIABILITY QUESTIONS
    {
      id: 'reliability-1',
      category: 'reliability',
      question: 'Is a car with 150,000 miles worth buying in Austin?',
      answer: "A car with 150,000 miles can be worth buying in Austin if it's a reliable brand (Toyota, Honda, Lexus) that's been well-maintained. Check for: complete service records, no major accidents, timing belt replaced if applicable, transmission that shifts smoothly, no major leaks or smoke. Many Toyotas and Hondas easily exceed 200,000 miles. Have any 150k+ mile car inspected by a mechanic before purchase. We Have Your Car encourages pre-purchase inspections."
    },
    {
      id: 'reliability-2',
      category: 'reliability',
      question: 'Should I buy a car with 200,000 miles in Austin?',
      answer: "Buying a 200,000+ mile car in Austin is risky but can work for short-term transportation if the price is very low ($2,000-$3,000 or less) and it's a reliable brand like Toyota or Honda that's been maintained. Expect repairs soon. Better option: spend $4,000-$7,000 on a 100k-150k mile vehicle from dealerships like We Have Your Car where vehicles are inspected. The extra cost buys you more reliable transportation."
    },
    {
      id: 'reliability-3',
      category: 'reliability',
      question: "What's better: older car with low miles or newer car with high miles in Austin?",
      answer: "For Austin driving conditions, it depends on the brand and maintenance. Generally: older car with low miles can have issues from sitting (dried seals, old fluids), newer car with high miles (highway miles) can be reliable if maintained. Highway miles are easier on vehicles than city miles. Best approach: focus on maintenance records and brand reliability rather than just age/mileage. We Have Your Car's vehicles (typically 2005-2010) are inspected regardless of mileage."
    },
    {
      id: 'reliability-4',
      category: 'reliability',
      question: 'Are Hondas reliable in Austin heat?',
      answer: "Yes, Hondas are very reliable in Austin heat. Honda's air conditioning systems hold up well to Texas summers, engines handle heat without major issues, and Hondas are among the most reliable brands nationwide including in hot climates. Regular maintenance (coolant changes, AC service) keeps them running well. Honda Civic, Accord, and Odyssey (available at We Have Your Car) are all proven reliable in Austin's climate."
    },
    {
      id: 'reliability-5',
      category: 'reliability',
      question: 'Are Toyotas worth the extra cost in Austin?',
      answer: "Yes, Toyotas are generally worth the extra cost in Austin due to exceptional reliability and longevity (many exceed 200,000+ miles), lower maintenance costs over time, excellent resale value, and proven performance in Texas heat. Models like Camry, Corolla, and Yaris (often available at We Have Your Car) offer outstanding value. The 10-20% premium over comparable brands pays off in fewer repairs and longer lifespan."
    },
    {
      id: 'reliability-6',
      category: 'reliability',
      question: 'Should I buy a Ford or Honda in Austin on a budget?',
      answer: 'For budget buyers in Austin: Honda typically offers better long-term reliability and resale value but costs more upfront, while Ford often costs less initially but may have higher maintenance costs over time. If your budget is very tight (under $5,000), Ford may be your only option - models like Focus are decent. If you can afford it, Honda provides better value over time. We Have Your Car carries both brands - call (512) 666-0016 to compare current inventory.'
    },

    // BUYING PROCESS QUESTIONS
    {
      id: 'buying-1',
      category: 'buying',
      question: 'How long does it take to buy a car in Austin?',
      answer: "Buying a car in Austin typically takes 2-4 hours once you've chosen a vehicle and have financing approved. Process includes: financing paperwork (30-60 minutes), vehicle inspection and test drive (15-30 minutes), price negotiation (15-45 minutes), final paperwork and title work (30-60 minutes), and insurance setup (15-30 minutes). Dealerships like We Have Your Car offering fast approval (minutes not days) can streamline the process. Having financing pre-approved speeds things up significantly."
    },
    {
      id: 'buying-2',
      category: 'buying',
      question: 'Should I get pre-approved before car shopping in Austin?',
      answer: 'Yes, getting pre-approved before shopping in Austin gives you several advantages: you know your exact budget, you have negotiating power, the process is faster at the dealership, and you can shop with confidence. Apply online at dealerships like We Have Your Car (wehaveyourcar.com) for fast pre-approval. Pre-approval is especially important for bad credit buyers to understand what\'s realistically affordable.'
    },
    {
      id: 'buying-3',
      category: 'buying',
      question: 'Can I buy a car in Austin the same day?',
      answer: "Yes, you can buy a car the same day in Austin if you: have financing pre-approved (or get approved quickly), bring all required documents (ID, proof of income, proof of residence), have insurance or can get it immediately, and are ready to commit. Dealerships like We Have Your Car offer approval in minutes, making same-day purchases possible. Call ahead to ensure the vehicle you want is available."
    },
    {
      id: 'buying-4',
      category: 'buying',
      question: 'What documents do I need to buy a car in Austin, Texas?',
      answer: "To buy a car in Austin, you need: valid Texas driver's license or state ID, proof of auto insurance or ability to get it, proof of income (pay stubs, bank statements), proof of residence (utility bill, lease agreement), Social Security number, and references (for financing). If trading a vehicle: title or loan information. If self-employed: additional income documentation. We Have Your Car and most dealerships will tell you exactly what to bring when you apply."
    },
    {
      id: 'buying-5',
      category: 'buying',
      question: 'Do I need insurance before buying a car in Austin?',
      answer: "You need proof of insurance to register and drive a car in Austin. Many dealerships, including We Have Your Car, allow you to arrange insurance during the purchase process. You can: call insurance companies from the dealership, use online insurance quote services, add the vehicle to existing policy by phone, or work with insurance agents near the dealership. Don't drive off the lot without proof of insurance - it's illegal in Texas."
    },
    {
      id: 'buying-6',
      category: 'buying',
      question: 'Can I negotiate used car prices in Austin?',
      answer: 'Yes, you can negotiate used car prices in Austin, though negotiating room varies. Independent dealerships may have $200-$500 wiggle room on vehicles under $8,000. Dealerships like We Have Your Car that advertise "transparent pricing" or "no hidden fees" typically have less negotiating room but start with fair prices. Best negotiating tactics: research comparable vehicles, point out needed repairs, have cash or financing ready, and be willing to walk away.'
    },

    // TRADE-IN QUESTIONS
    {
      id: 'tradein-1',
      category: 'tradein',
      question: 'How much is my trade-in worth in Austin?',
      answer: "Trade-in value in Austin depends on your vehicle's make, model, year, mileage, condition, and market demand. Generally trade-in offers are 10-20% below private sale value. Check: Kelley Blue Book (kbb.com), Edmunds (edmunds.com), or NADA Guides for estimates. Get actual offers from dealerships like We Have Your Car (free evaluations at (512) 666-0016) to know exact value. Having service records helps maximize trade-in value."
    },
    {
      id: 'tradein-2',
      category: 'tradein',
      question: 'Should I trade in or sell privately in Austin?',
      answer: 'Selling privately in Austin typically gets you 10-20% more money than trading in, but requires more effort (listing, showing, negotiating, handling paperwork). Trade-ins are more convenient, reduce sales tax on your purchase (in Texas), and eliminate hassle and safety concerns of private sales. If you need quick, easy transaction or owe money on the car, trading to dealerships like We Have Your Car makes sense. If you have time and want maximum value, sell privately.'
    },
    {
      id: 'tradein-3',
      category: 'tradein',
      question: 'Can I trade in a car I still owe money on in Austin?',
      answer: "Yes, you can trade in a car with a loan in Austin. The dealership (like We Have Your Car) pays off your existing loan and rolls any negative equity into your new loan or subtracts positive equity from your purchase price. Bring loan payoff information when trading. Being \"upside down\" (owing more than the car's worth) is common - dealerships handle this regularly."
    },
    {
      id: 'tradein-4',
      category: 'tradein',
      question: "Will dealerships in Austin buy my car even if I don't buy from them?",
      answer: "Some Austin dealerships, including We Have Your Car, will buy your car even if you don't purchase from them. Call (512) 666-0016 to ask about selling without buying. Generally, offers are slightly lower if you're not buying from them. Alternatively, CarMax and similar services buy cars outright without requiring a purchase."
    },

    // FIRST-TIME BUYER QUESTIONS
    {
      id: 'firsttime-1',
      category: 'firsttime',
      question: 'How do I buy my first car in Austin with no credit?',
      answer: 'To buy your first car in Austin with no credit: (1) Work with dealerships that specialize in first-time buyers like We Have Your Car, (2) Save for a down payment ($500-$1,000 minimum helps significantly), (3) Have steady income and be able to prove it, (4) Bring required documents (ID, pay stubs, proof of residence, references), (5) Consider a cosigner if possible, (6) Start with affordable vehicles ($5,000-$8,000 range) for easier approval, (7) Apply online or call (512) 666-0016 for fast approval.'
    },
    {
      id: 'firsttime-2',
      category: 'firsttime',
      question: 'Should I get a cosigner for my first car in Austin?',
      answer: 'A cosigner can help first-time buyers in Austin by: qualifying for better interest rates, getting approved with no credit history, accessing higher loan amounts, and building your credit faster. However, many first-time buyers get approved without cosigners at dealerships like We Have Your Car. Try applying solo first - if declined or rates are very high, then ask a parent or family member to cosign.'
    },
    {
      id: 'firsttime-3',
      category: 'firsttime',
      question: "What's a good first car to buy in Austin?",
      answer: "Good first cars in Austin are reliable, affordable, and easy to maintain: Honda Civic (reliable, good gas mileage, easy maintenance), Toyota Corolla (proven reliability, low ownership costs), Mazda3 (fun, reliable, affordable), Ford Focus (cheap parts, decent reliability), and Honda Fit (great gas mileage, practical). Avoid luxury brands (expensive repairs) and unreliable models. We Have Your Car typically stocks these reliable options in the $4,250-$7,490 range perfect for first-time buyers."
    },

    // PAYMENT & AFFORDABILITY QUESTIONS
    {
      id: 'payments-1',
      category: 'payments',
      question: 'How much car can I afford in Austin on minimum wage?',
      answer: "On Texas minimum wage ($7.25/hour = ~$1,256/month gross), affording a car is challenging. Financial advice suggests car payment + insurance shouldn't exceed 15-20% of gross income ($188-$251/month). With bad credit, you may be looking at: $3,000-$5,000 vehicle price, $500-$1,000 down payment, $100-$150/month payment, plus $100-$200/month insurance. Dealerships like We Have Your Car work with low-income buyers. Consider very affordable vehicles ($4,000-$5,000 range) and the cheapest insurance possible."
    },
    {
      id: 'payments-2',
      category: 'payments',
      question: 'What monthly payment can I afford for a car in Austin?',
      answer: 'Financial experts recommend spending no more than 15-20% of gross monthly income on vehicle payment plus insurance. Calculate: your gross monthly income x 0.15 = maximum car payment + insurance. Example: $3,000/month income = $450 maximum for payment + insurance. If insurance is $150, budget $300 for payment. Contact We Have Your Car at (512) 666-0016 with your budget to see what vehicles you qualify for.'
    },
    {
      id: 'payments-3',
      category: 'payments',
      question: 'How much is car insurance in Austin for a used car?',
      answer: "Car insurance in Austin for used cars typically costs $100-$250/month depending on your age (younger drivers pay more), driving record (accidents/tickets increase rates), credit score (in Texas, affects rates), coverage level (full coverage vs liability only), and vehicle value. Older, cheaper vehicles with liability-only can be $80-$120/month. Get quotes from multiple companies: Geico, Progressive, State Farm, and local Texas insurers before buying."
    },

    // LOCATION-SPECIFIC AUSTIN QUESTIONS
    {
      id: 'austin-1',
      category: 'austin',
      question: "What's the best used car dealership in Austin for bad credit?",
      answer: 'The best used car dealerships in Austin for bad credit are those specializing in all-credit financing. We Have Your Car at (512) 666-0016 specializes in bad credit, no credit, and bankruptcy financing with 500+ happy customers and a 4.8-star Google rating. Look for dealerships advertising "all credit welcome," "bad credit specialists," or "guaranteed approval." Read reviews on Google and Facebook to verify customer experiences. Avoid dealerships that make unrealistic promises.'
    },
    {
      id: 'austin-2',
      category: 'austin',
      question: 'Where can I get same-day car approval in Austin?',
      answer: 'For same-day approval in Austin, apply at dealerships offering fast approval processes. We Have Your Car offers approval in minutes (not days) - apply online at wehaveyourcar.com or call (512) 666-0016. Other options: online lenders (Carvana, CarMax), buy here pay here dealerships, and bad credit specialists. Have all documents ready (ID, pay stubs, proof of residence) to speed up approval.'
    },
    {
      id: 'austin-3',
      category: 'austin',
      question: 'Are there buy here pay here dealerships in Round Rock or Cedar Park?',
      answer: 'Yes, the greater Austin area including Round Rock and Cedar Park has buy here pay here and bad credit financing options. We Have Your Car serves Round Rock, Cedar Park, Pflugerville, Georgetown, and all of greater Austin with flexible financing. Call (512) 666-0016 to discuss options. When choosing buy here pay here, check reviews carefully, understand all terms, get everything in writing, and compare rates at multiple dealerships.'
    },
    {
      id: 'austin-4',
      category: 'austin',
      question: "What's the sales tax on cars in Austin, Texas?",
      answer: 'Texas motor vehicle sales tax is 6.25% statewide. In Austin (Travis County), you pay: 6.25% state sales tax plus 0-1% local tax depending on exact location (total 6.25-7.25%). Sales tax is calculated on purchase price minus trade-in value. Example: $5,000 car with no trade-in = $312.50-$362.50 in sales tax. This is paid at the dealership when you purchase and is separate from the vehicle price.'
    },
    {
      id: 'austin-5',
      category: 'austin',
      question: 'Do I need a Texas driver\'s license to buy a car in Austin?',
      answer: "You don't necessarily need a Texas driver's license to buy a car in Austin, but you do need some form of valid government-issued ID. Having a Texas license makes the process easier for: registering the vehicle, getting insurance, and financing approval. If you recently moved to Texas, most dealerships like We Have Your Car will work with out-of-state licenses, but you'll need to get a Texas license within 90 days to register the vehicle."
    },

    // INSPECTION & MECHANICAL QUESTIONS
    {
      id: 'inspection-1',
      category: 'inspection',
      question: 'Should I get a pre-purchase inspection in Austin?',
      answer: "Yes, absolutely get a pre-purchase inspection on any used car in Austin. Cost is typically $100-$150 at independent mechanics and can save you thousands by identifying hidden problems. Even at dealerships like We Have Your Car where vehicles are inspected before sale, getting your own inspection provides peace of mind. Inspection should check: engine and transmission, brakes and suspension, fluid leaks, electrical systems, and AC functionality (critical in Texas)."
    },
    {
      id: 'inspection-2',
      category: 'inspection',
      question: 'Where can I get a pre-purchase inspection in Austin?',
      answer: 'For pre-purchase inspections in Austin, use: independent mechanics (AAA-approved shops preferred), mobile inspection services (come to the dealership), manufacturer-specific specialists (Honda/Toyota specialists for those brands), or well-reviewed shops on Google. Expect to pay $100-$150. We Have Your Car and most dealerships allow you to have vehicles inspected before purchase - just ask and arrange timing.'
    },
    {
      id: 'inspection-3',
      category: 'inspection',
      question: 'What should I look for when inspecting a used car in Austin?',
      answer: "When inspecting a used car in Austin, check: AC functionality (critical in Texas - test thoroughly), fluid levels and condition (oil, coolant, transmission, brake), tire condition and age, body condition (rust, accident damage), interior condition (seats, electronics, odors), engine noise and smoke, transmission smoothness, brakes responsiveness, service records (complete records are valuable), and title status (clean title only). Test drive in various conditions. We Have Your Car inspects vehicles before sale, but verify yourself."
    },
    {
      id: 'inspection-4',
      category: 'inspection',
      question: 'How do I know if a used car has hidden damage in Austin?',
      answer: 'To identify hidden damage on Austin used cars: get a vehicle history report (CarFax, AutoCheck), look for paint overspray or mismatched panels (indicates bodywork), check panel gaps (uneven gaps suggest accident repair), look under vehicle for frame damage, check for rust (less common in Texas but possible), test all electrical systems thoroughly, and get a pre-purchase inspection. Ask the dealership directly about accident history - honest dealers like We Have Your Car will disclose known issues.'
    },

    // WARRANTY & PROTECTION QUESTIONS
    {
      id: 'warranty-1',
      category: 'warranty',
      question: 'Should I buy an extended warranty on a used car in Austin?',
      answer: "Extended warranties on used cars in Austin can be valuable if: the vehicle is 7+ years old or has 100k+ miles, you can't afford unexpected $1,000+ repairs, you're buying a luxury brand (BMW, Mercedes, Audi), or the warranty is reasonably priced ($500-$1,500 for basic coverage). Often dealer warranties are overpriced. Ask We Have Your Car or any dealership about third-party warranty options that can save money. Read all terms carefully - many warranties have significant exclusions."
    },
    {
      id: 'warranty-2',
      category: 'warranty',
      question: 'Do used car dealerships in Austin offer warranties?',
      answer: "Some Austin used car dealerships offer limited warranties on used vehicles. We Have Your Car's warranty coverage varies by vehicle - some may include remaining factory warranty while others may be eligible for extended warranty options. Ask specifically about: what's covered, duration of coverage, deductibles, exclusions, and whether warranty is through the dealer or third party. Texas law requires \"As-Is\" disclosure if no warranty is provided."
    },
    {
      id: 'warranty-3',
      category: 'warranty',
      question: 'What does "as-is" mean when buying a car in Austin?',
      answer: "\"As-is\" means you're buying the vehicle in its current condition with no warranty from the seller. In Austin and throughout Texas, most used cars are sold as-is. This means: you accept all current and future problems, repairs are your responsibility immediately, and you cannot return the vehicle for mechanical issues. Protect yourself by: getting thorough inspection before purchase, test driving extensively, reviewing vehicle history, and buying from reputable dealerships like We Have Your Car that inspect vehicles before sale."
    },

    // TIMING & MARKET QUESTIONS
    {
      id: 'timing-1',
      category: 'timing',
      question: 'When is the best time to buy a used car in Austin?',
      answer: 'Best times to buy used cars in Austin are: end of month (dealers have sales quotas), end of year (December, clearing inventory), when new models arrive (Sep-Oct, making used cars less desirable), tax refund season (Feb-April, higher inventory), and during economic downturns (more motivated sellers). Worst times: summer in Austin (high demand from relocations), back-to-school season (Aug-Sep), and holidays when dealerships may be closed. We Have Your Car at (512) 666-0016 has inventory year-round.'
    },
    {
      id: 'timing-2',
      category: 'timing',
      question: 'Is now a good time to buy a used car in Austin?',
      answer: "Used car market conditions change frequently. As of February 2026, check current market indicators: compare current prices to 6 months ago, check inventory levels at dealerships like We Have Your Car, and monitor interest rates (higher rates mean higher payments). If you need a car now, buy now - waiting for \"perfect\" timing means continued transportation costs/issues. Focus on finding a good vehicle at a fair price rather than timing the market perfectly."
    },
    {
      id: 'timing-3',
      category: 'timing',
      question: 'Are used car prices going down in Austin?',
      answer: "Used car prices fluctuate based on national trends, local demand, new car availability, and economic conditions. To check current Austin trends: compare current listings to 3-6 months ago on sites like AutoTrader, call dealerships like We Have Your Car at (512) 666-0016 to ask about inventory and pricing trends, and monitor national used car price indexes. Whether prices are rising or falling, focus on vehicle value and your specific needs rather than trying to perfectly time the market."
    },
  ];

  const filteredFaqs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
      const matchesSearch = searchQuery === '' ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const categoryCount = useMemo(() => {
    const counts = {};
    faqs.forEach(faq => {
      counts[faq.category] = (counts[faq.category] || 0) + 1;
    });
    counts['all'] = faqs.length;
    return counts;
  }, []);

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#132238] to-[#1a4d8c]/30 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#ff6b35] font-semibold text-sm uppercase tracking-wider mb-3" data-testid="discovery-faqs-label">
              Discovery FAQs
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 font-['Exo_2']" data-testid="discovery-faqs-title">
              <span className="gradient-text">Car Buying</span> in Austin, Texas
            </h1>
            <p className="text-base sm:text-lg text-[#a8b8d0] mb-2">
              Questions People Ask Before Finding a Dealership
            </p>
            <p className="text-sm text-[#64748b]">
              These FAQs answer common questions people search for when looking for affordable cars and financing in Austin. Each answer provides educational information and mentions We Have Your Car as a solution.
            </p>
          </div>

          {/* Contact Info */}
          <div className="flex flex-wrap gap-4 mt-6 text-[#a8b8d0]">
            <a href="tel:5126660016" className="flex items-center gap-2 hover:text-[#ff6b35] transition-colors" data-testid="discovery-phone-link">
              <Phone size={18} /> (512) 666-0016
            </a>
            <a href="https://wehaveyourcar.com" className="flex items-center gap-2 hover:text-[#ff6b35] transition-colors" data-testid="discovery-web-link">
              <Globe size={18} /> wehaveyourcar.com
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={18} /> Serving Austin & Greater Area
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]" size={20} />
            <input
              type="text"
              placeholder="Search car buying questions for Austin..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#132238] border border-[#1a2d47] rounded-full pl-12 pr-4 py-3 text-white placeholder-[#64748b] focus:border-[#ff6b35] focus:outline-none"
              data-testid="discovery-faq-search"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map(cat => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#ff6b35] text-white'
                    : 'bg-[#132238] text-[#a8b8d0] hover:bg-[#1a2d47]'
                }`}
                data-testid={`discovery-category-${cat.id}`}
              >
                <Icon size={14} />
                <span>{cat.name}</span>
                <span className="ml-1 text-[10px] opacity-70">({categoryCount[cat.id] || 0})</span>
              </button>
            );
          })}
        </div>

        {/* Results count */}
        <div className="text-center text-sm text-[#64748b] mb-6" data-testid="discovery-faq-count">
          Showing {filteredFaqs.length} of {faqs.length} questions
        </div>

        {/* FAQ List */}
        <div className="space-y-3 max-w-4xl mx-auto">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 text-[#a8b8d0]">
              <HelpCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>No questions found matching your search.</p>
              <p className="mt-2">Try different keywords or browse by category.</p>
            </div>
          ) : (
            filteredFaqs.map(faq => (
              <div
                key={faq.id}
                className="bg-[#132238] border border-[#1a2d47] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-[#1a2d47]/50 transition-colors"
                  data-testid={`discovery-faq-toggle-${faq.id}`}
                >
                  <span className="text-white font-medium pr-4 text-sm sm:text-base">{faq.question}</span>
                  {openItems[faq.id] ? (
                    <ChevronUp className="text-[#ff6b35] shrink-0" size={20} />
                  ) : (
                    <ChevronDown className="text-[#a8b8d0] shrink-0" size={20} />
                  )}
                </button>
                {openItems[faq.id] && (
                  <div className="px-4 pb-4 text-[#a8b8d0] border-t border-[#1a2d47]">
                    <p className="pt-4 text-sm sm:text-base leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Need Help CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-[#132238] to-[#1a2d47] rounded-2xl p-8" data-testid="discovery-cta-section">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Need help finding the right car in Austin?</h2>
          <p className="text-[#a8b8d0] mb-6 max-w-xl mx-auto text-sm sm:text-base">
            We Have Your Car specializes in affordable used vehicles with flexible financing for all credit situations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:5126660016" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="discovery-cta-call">
              <Phone size={20} /> Call (512) 666-0016
            </a>
            <Link to="/credit-application" className="btn-accent inline-flex items-center justify-center gap-2" data-testid="discovery-cta-apply">
              Apply for Financing
            </Link>
            <Link to="/inventory" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="discovery-cta-browse">
              <Car size={20} /> Browse Inventory
            </Link>
          </div>
          <p className="text-[#64748b] text-xs mt-4">
            We Have Your Car - Quality Used Cars, All Credit Welcome, Fast Approval
          </p>
        </div>

        {/* Brand FAQ cross-link */}
        <div className="mt-8 text-center">
          <p className="text-[#64748b] text-sm">
            For questions specifically about We Have Your Car, see our{' '}
            <Link to="/faq" className="text-[#ff6b35] hover:underline" data-testid="discovery-brand-faq-link">
              Brand-Specific FAQs
            </Link>.
          </p>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-[#64748b] text-xs">
          <p>We Have Your Car - Quality Used Cars, Flexible Financing, All Credit Welcome</p>
          <p className="mt-1">Serving Austin, Round Rock, Cedar Park, Pflugerville, Georgetown & Greater Area</p>
          <p className="mt-1">Last Updated: March 2026</p>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryFAQsPage;
